function initBuildABox() {
  class BuildABox {
    constructor(containerId = "build-a-box") {
      // DOM elements
      this.container = document.getElementById(containerId);
      if (!this.container) return;

      this.buildABoxVariantId = parseInt(
        this.container.dataset.buildABoxVariantId,
        10,
      );
      this.countEl = document.getElementById("box-count");
      this.babTag = document.getElementById("js-bab-tag");
      this.progressBar = document.getElementById("js-bab-progress-bar");
      this.addBoxBtn = document.getElementById("add-box-to-cart");
      this.packSizeRadios = document.querySelectorAll(
        'input[name="pack-size"]',
      );
      this.totalPriceEl = document.getElementById("js-bab-box-cart__total");
      this.boxListEl = document.getElementById("js-bab-box-cart__items");
      this.pageHandle = this.container.dataset.pageHandle;

      // Track if the user has unsaved changes (only relevant when editing)
      this.unsavedChanges = false;

      // State
      this.box = {}; // variantId -> { qty, title, img, priceCents, priceFormatted }
      const checkedPackSize = document.querySelector(
        'input[name="pack-size"]:checked',
      );
      this.packSizeValue = checkedPackSize?.value || "1";
      this.previousPackSizeValue = this.packSizeValue;

      // 👇 NEW: Parse edit_bundle_id from URL if present
      const urlParams = new URLSearchParams(window.location.search);
      this.editBundleId = urlParams.get("edit_bundle_id");

      // Initialization
      this.initProductItems();
      this.updateAddButtons();
      this.updateBoxList();
      this.initAddToBoxButtons();
      this.initPackSizeRadios();
      this.initAddBoxToCart();
      this.initTabs();
      this.initBoxToggle();

      // 👇 NEW: If editing, load that bundle
      if (this.editBundleId) {
        this.loadExistingBundle(this.editBundleId);
      }
    }

    // -------------------------------
    // NEW: Load existing bundle for editing
    // -------------------------------
    async loadExistingBundle(bundleId) {
      console.log(`Editing existing bundle: ${bundleId}`);

      try {
        // 🔧 If you have a cart or API call to fetch bundle contents, do it here:
        const response = await fetch("/cart.js");
        const cart = await response.json();

        const bundleItems = cart.items.filter(
          (item) => item.properties?._bundle_id === bundleId,
        );

        if (bundleItems.length === 0) {
          console.warn("No bundle found in cart with this ID.");
          return;
        }

        const parent = bundleItems.find(
          (item) => item.properties?._bundle_parent,
        );
        if (parent?.properties?.pack_size) {
          this.packSizeValue = parent.properties.pack_size;

          this.packSizeRadios.forEach((radio) => {
            radio.checked = radio.value === this.packSizeValue;
          });

          this.updateAddButtons();
        }

        // Add child items to this.box
        bundleItems
          .filter((item) => !item.properties?._bundle_parent)
          .forEach((item) => {
            this.box[item.id] = {
              qty: item.quantity,
              title: item.title,
              variantOptions: item.variant_title || "",
              img: item.image || "",
              priceCents: Math.round(item.final_price / item.quantity),
              priceFormatted: item.price,
            };
          });

        this.updateBoxList();
        if (this.editBundleId && this.addBoxBtn) {
          this.addBoxBtn.textContent = "Update Box";
        }

        console.log("Bundle restored for editing:", this.box);
      } catch (err) {
        console.error("Error loading existing bundle:", err);
      }
    }

    // -------------------------------
    // Helpers
    // -------------------------------
    formatPrice(cents, formatted = null) {
      const numberPart = (cents / 100).toFixed(2);

      // Only use replace if `formatted` is a string that contains digits
      if (typeof formatted === "string" && /[\d.,]+/.test(formatted)) {
        return formatted.replace(/[\d.,]+/, numberPart);
      }

      return `$${numberPart}`;
    }

    getMaxItems() {
      return parseInt(this.packSizeValue, 10);
    }

    // -------------------------------
    // Product Variant Handling
    // -------------------------------
    initProductItems() {
      document.querySelectorAll(".js-bab-product-item").forEach((wrapper) => {
        const variantSelect = wrapper.querySelector(".js-bab-variant-select");
        const optionSelects = wrapper.querySelectorAll(
          ".js-bab-product-option",
        );
        const priceElement = wrapper.querySelector(".js-bab-price");
        const imageElement = wrapper.querySelector("img");

        if (!variantSelect || !optionSelects.length) return;

        const comparePriceElement = wrapper.querySelector(
          ".js-bab-compare-price",
        );

        optionSelects.forEach((select) => {
          select.addEventListener("change", () => {
            const selectedValues = Array.from(optionSelects).map((sel) =>
              sel.value.toLowerCase(),
            );

            Array.from(variantSelect.options).forEach((opt) => {
              const variantText = opt.text.toLowerCase();
              const matchesAll = selectedValues.every((val) =>
                variantText.includes(val),
              );

              if (matchesAll) {
                variantSelect.value = opt.value;

                const price = opt.dataset.price;
                const compareAtPrice = opt.dataset.compareAtPrice;
                const compareAtPriceCents = parseInt(
                  opt.dataset.compareAtPriceCents || "0",
                  10,
                );
                const priceCents = parseInt(opt.dataset.priceCents || "0", 10);
                const imageUrl = opt.dataset.imageId;
                const variantTitle = opt.dataset.variantTitle;

                if (priceElement) priceElement.textContent = price;

                if (comparePriceElement) {
                  if (
                    compareAtPriceCents > 0 &&
                    compareAtPriceCents > priceCents
                  ) {
                    comparePriceElement.textContent = compareAtPrice;
                    comparePriceElement.style.display = "";
                  } else {
                    comparePriceElement.textContent = "";
                    comparePriceElement.style.display = "none";
                  }
                }

                if (imageElement && imageUrl) {
                  imageElement.src = imageUrl;
                  imageElement.alt = variantTitle || "Product image";
                }
              }
            });
          });
        });
      });
    }

    // -------------------------------
    // Box State & UI Updates
    // -------------------------------
    updateAddButtons() {
      document.querySelectorAll(".js-bab-product-option").forEach((select) => {
        select.querySelectorAll("option").forEach((option) => {
          option.selected = option.value.includes(this.packSizeValue);
        });
        select.dispatchEvent(new Event("change", { bubbles: true }));
      });
    }

    updateBoxList() {
      if (!this.boxListEl) return;

      this.boxListEl.innerHTML = "";
      let totalItems = 0;
      let totalCents = 0;
      const maxItems = this.getMaxItems();

      Object.entries(this.box).forEach(([variantId, item]) => {
        totalItems += item.qty;
        totalCents += item.priceCents * item.qty;

        const li = document.createElement("li");
        li.classList.add("box-item");
        li.innerHTML = `
			<div class="c-box-item__image">
			<img src="${item.img}" alt="${item.title}" width="95" />
			<div class="c-box-item__qty-tag">${item.qty}</div>
			</div>
			<div>
			<p class="c-box-item__title">${item.title}</p>
			<div class="c-box-item__meta">
				<p class="line-option">${item.variantOptions || ""}</p>
				<p class="line-total">${this.formatPrice(
          item.priceCents * item.qty,
          item.priceFormatted,
        )}</p>
			</div>
			<div class="c-quantity-selector">
				<button type="button" class="js-qty-btn" data-change="-1">-</button>
				<input class="cart-quantity-input" type="text" value="${item.qty}" min="0">
				<button type="button" class="js-qty-btn" data-change="1">+</button>
			</div>
			<button class="remove-item">
				<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
				<mask id="mask0" style="mask-type:alpha" maskUnits="userSpaceOnUse" x="0" y="0" width="24" height="24">
					<rect width="24" height="24" fill="#D9D9D9"/>
				</mask>
				<g mask="url(#mask0)">
					<path d="M8.22756 16.8365L7.16406 15.773L10.9371 12L7.16406 8.25198L8.22756 7.18848L12.0006 10.9615L15.7486 7.18848L16.8121 8.25198L13.0391 12L16.8121 15.773L15.7486 16.8365L12.0006 13.0635L8.22756 16.8365Z" fill="#1C355E"/>
				</g>
				</svg>
			</button>
			</div>
		`;

        li.querySelectorAll(".js-qty-btn").forEach((btn) =>
          btn.addEventListener("click", () =>
            this.changeQty(variantId, parseInt(btn.dataset.change, 10)),
          ),
        );

        li.querySelector(".cart-quantity-input").addEventListener(
          "change",
          (e) =>
            this.changeQty(variantId, 0, parseInt(e.target.value || "0", 10)),
        );

        li.querySelector(".remove-item").addEventListener("click", () => {
          delete this.box[variantId];
          this.updateBoxList();
        });

        this.boxListEl.appendChild(li);
      });

      if (this.countEl) this.countEl.textContent = `${totalItems}/${maxItems}`;
      if (this.babTag) this.babTag.textContent = `${maxItems}`;
      if (this.progressBar)
        this.progressBar.style.width = `${Math.min(
          (totalItems / maxItems) * 100,
          100,
        )}%`;
      if (this.totalPriceEl)
        this.totalPriceEl.textContent = `Total: ${this.formatPrice(totalCents)}`;
      if (this.addBoxBtn)
        this.addBoxBtn.disabled = totalItems === 0 || totalItems < maxItems;

      if (this.boxListEl)
        this.boxListEl.scrollTop = this.boxListEl.scrollHeight;
    }

    changeQty(variantId, delta = 0, directValue = null) {
      const item = this.box[variantId];
      if (!item) return;

      let newQty = directValue !== null ? directValue : item.qty + delta;
      if (newQty < 0) newQty = 0;

      const maxItems = this.getMaxItems();
      const totalBefore = Object.values(this.box).reduce(
        (sum, i) => sum + i.qty,
        0,
      );
      const deltaCheck = directValue !== null ? newQty - item.qty : delta;

      if (deltaCheck > 0 && totalBefore + deltaCheck > maxItems) {
        alert(`You can only add up to ${maxItems} items!`);
        this.updateBoxList();
        return;
      }

      if (newQty === 0) delete this.box[variantId];
      else this.box[variantId].qty = newQty;

      this.unsavedChanges = true;
      this.updateBoxList();
    }

    // -------------------------------
    // Event Bindings
    // -------------------------------
    initAddToBoxButtons() {
      document.querySelectorAll(".js-add-to-box").forEach((button) => {
        button.addEventListener("click", () => {
          const productItem = button.closest(".js-bab-product-item");
          if (!productItem) return;

          const qtyInput = productItem.querySelector('input[name="quantity"]');
          const qty = Math.max(1, parseInt(qtyInput?.value || "1", 10));

          const variantSelect = productItem.querySelector(
            ".js-bab-variant-select",
          );
          if (!variantSelect) return;
          const selectedOption =
            variantSelect.options[variantSelect.selectedIndex];
          if (!selectedOption) return;

          const variantId = selectedOption.value;
          const priceCents = parseInt(selectedOption.dataset.priceCents, 10);
          const img = selectedOption.dataset.imageId;
          const title = selectedOption.dataset.productTitle;
          const variantOptions = selectedOption.dataset.variantOptions;
          const priceFormatted = selectedOption.dataset.price;
          const selectedVariantTitle = selectedOption.dataset.variantTitle;

          if (!selectedVariantTitle.includes(this.packSizeValue)) return;

          const totalCurrent = Object.values(this.box).reduce(
            (a, b) => a + b.qty,
            0,
          );
          if (totalCurrent + qty > this.getMaxItems()) {
            alert(`You can only add up to ${this.getMaxItems()} items!`);
            return;
          }

          this.box[variantId] = {
            qty: (this.box[variantId]?.qty || 0) + qty,
            title,
            variantOptions,
            img,
            priceCents,
            priceFormatted,
          };

          this.unsavedChanges = true;
          this.updateBoxList();
        });
      });
    }

    initPackSizeRadios() {
      this.packSizeRadios.forEach((radio) => {
        radio.addEventListener("change", () => {
          const selectedRadio = document.querySelector(
            'input[name="pack-size"]:checked',
          );
          const newValue = selectedRadio?.value;
          if (!newValue) return;

          const totalItems = Object.values(this.box).reduce(
            (sum, item) => sum + item.qty,
            0,
          );
          if (
            totalItems > 0 &&
            !confirm(
              "Changing pack size will empty your current box. Are you sure?",
            )
          ) {
            document
              .querySelector(
                `input[name="pack-size"][value="${this.previousPackSizeValue}"]`,
              )
              ?.setAttribute("checked", true);
            return;
          }

          Object.keys(this.box).forEach((key) => delete this.box[key]);
          this.previousPackSizeValue = this.packSizeValue;
          this.packSizeValue = newValue;

          this.updateAddButtons();
          this.unsavedChanges = true;
          this.updateBoxList();
        });
      });
    }

    initAddBoxToCart() {
      if (!this.addBoxBtn) return;

      this.addBoxBtn.addEventListener("click", async () => {
        const totalItems = Object.values(this.box).reduce(
          (a, b) => a + b.qty,
          0,
        );
        if (totalItems === 0) return;

        // --- Determine bundle ID ---
        const editingExisting = !!this.editBundleId;
        const bundleId = editingExisting
          ? this.editBundleId
          : `bundle-${Date.now()}`;
        const handle = this.pageHandle;

        const items = [
          {
            id: this.buildABoxVariantId,
            quantity: 1,
            properties: {
              _bundle_id: bundleId,
              _bundle_parent: true,
              _page_handle: handle,
              pack_size: this.packSizeValue,
            },
          },
          ...Object.entries(this.box).map(([variantId, item]) => ({
            id: parseInt(variantId, 10),
            quantity: item.qty,
            properties: { _bundle_id: bundleId, _bundle_parent: false },
          })),
        ];

        try {
          // --- If editing, remove the old bundle first ---
          if (editingExisting) {
            const cartRes = await fetch("/cart.js");
            const cart = await cartRes.json();

            const oldBundleItems = cart.items.filter(
              (item) => item.properties?._bundle_id === this.editBundleId,
            );

            if (oldBundleItems.length > 0) {
              const updates = {};
              oldBundleItems.forEach((item) => {
                updates[item.key] = 0; // remove each line item
              });

              await fetch("/cart/update.js", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ updates }),
              });
              console.log(
                `Removed old bundle ${this.editBundleId} before re-adding.`,
              );
            }
          }

          // --- Add new (or updated) bundle ---
          const response = await fetch(
            `${window.Shopify.routes.root}cart/add.js`,
            {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ items }),
            },
          );
          if (!response.ok) throw new Error("Failed to add bundle");

          await response.json();
          console.log("Bundle added to cart!");
          // After updating the cart successfully
          this.unsavedChanges = false;

          // ✅ Remove edit_bundle_id from URL if present
          if (this.editBundleId) {
            const url = new URL(window.location);
            url.searchParams.delete("edit_bundle_id");
            window.history.replaceState({}, document.title, url.toString());
            this.editBundleId = null; // clear from instance too

            // Reset button text
            if (this.addBoxBtn) {
              this.addBoxBtn.textContent = "Add to Cart";
            }
          }

          // Refresh drawer once
          await window.CartDrawer.refresh();
          window.CartDrawer.open();

          Object.keys(this.box).forEach((key) => delete this.box[key]);
          this.updateBoxList();

          document
            .querySelectorAll('.js-bab-product-item input[name="quantity"]')
            .forEach((input) => (input.value = "1"));
        } catch (err) {
          console.error(err);
          alert("There was an error updating your bundle in the cart.");
        }
      });
    }

    initTabs() {
      document.querySelectorAll(".js-bab-tab-wrapper").forEach((component) => {
        const tabButtons = component.querySelectorAll(".tab-btn");
        const tabPanels = component.querySelectorAll(".tab-panel");
        const tabList = component.querySelector(".c-tabs-nav [role='tablist']");
        const nextButton = component.querySelector(".c-tabs-nav__scroll-next");

        nextButton?.addEventListener("click", () => {
          tabList?.scrollBy({
            left: Math.round(tabList.clientWidth * 0.75),
            behavior: "smooth",
          });
        });

        tabButtons.forEach((btn, index) => {
          btn.addEventListener("click", () => {
            tabButtons.forEach((b) => b.setAttribute("aria-selected", "false"));
            btn.setAttribute("aria-selected", "true");

            tabPanels.forEach((panel) => panel.classList.remove("active"));
            tabPanels[index].classList.add("active");
          });
        });
      });
    }

    initBoxToggle() {
      document.addEventListener("click", (e) => {
        const toggleBtn = e.target.closest(".js-bab-box-cart__toggle");
        const boxCartItems = document.querySelector("#js-bab-box-cart__items");
        if (!toggleBtn || !boxCartItems) return;
        boxCartItems.classList.toggle("is-open");
        toggleBtn.classList.toggle("is-open");
      });
    }
  }

  window.addEventListener("beforeunload", (e) => {
    if (
      window.buildABoxInstance?.unsavedChanges &&
      window.buildABoxInstance?.editBundleId
    ) {
      e.preventDefault();
      e.returnValue =
        "You have unsaved changes to your box. Are you sure you want to leave?";
    }
  });

  // -------------------------------
  // Initialize
  // -------------------------------
  document.addEventListener("DOMContentLoaded", () => {
    window.buildABoxInstance = new BuildABox();
  });
}

initBuildABox();

export { initBuildABox };
